import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Play,
  Pause,
  Trash2,
  Upload,
  Save,
  History,
  Lock,
  Plus,
  X,
  Sparkles,
  Loader2,
  Code,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import StatusBadge from "@/components/StatusBadge";
import EmptyState from "@/components/EmptyState";
import SendingWindowEditor from "@/components/SendingWindowEditor";
import CampaignDiagnose from "@/components/CampaignDiagnose";
import CampaignStatusPill from "@/components/CampaignStatusPill";
import ConfirmModal from "@/components/ConfirmModal";

export default function CampaignDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = !!user?.is_admin;
  const [tab, setTab] = useState("overview");
  const [campaign, setCampaign] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  // Stage 8B — Sequence Versioning. `seq` now holds the steps for the
  // currently-VIEWED version (which may not be the current/live version).
  // `seqVersion` is what the user has selected in the dropdown,
  // `currentSeqVersion` is what the campaign is actively running.
  const [seq, setSeq] = useState([]);
  const [seqVersion, setSeqVersion] = useState(null);
  const [currentSeqVersion, setCurrentSeqVersion] = useState(1);
  const [seqVersions, setSeqVersions] = useState([]);
  const [seqDraft, setSeqDraft] = useState([]);
  const [seqDirty, setSeqDirty] = useState(false);
  const [seqSaving, setSeqSaving] = useState(false);
  const [leads, setLeads] = useState([]);
  const [csv, setCsv] = useState("");
  // Strike-1: async AI icebreaker enrichment state.
  const [enrichBusy, setEnrichBusy] = useState(false);
  const [enrichInfo, setEnrichInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  // Strike 8C — state-driven confirmations (replaces window.confirm).
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  // Version-switcher unsaved-changes guard.
  const [pendingVersionSwitch, setPendingVersionSwitch] = useState(null);

  const isViewingLegacy =
    seqVersion !== null && seqVersion !== currentSeqVersion;
  // Sequence becomes immutable once the campaign reaches a terminal status.
  // active / paused / draft → editable on the current version; completed
  // → fully read-only.
  const sequenceLocked = campaign?.status === "completed" || isViewingLegacy;

  const loadSequence = async (versionOverride) => {
    const params = versionOverride ? { version: versionOverride } : {};
    const [seqResp, versionsResp] = await Promise.all([
      api.get(`/campaigns/${id}/sequence`, { params }),
      api.get(`/campaigns/${id}/sequence/versions`).catch(() => null),
    ]);
    const payload = seqResp.data || {};
    // Defensive: backend may return either the new {version, steps} shape
    // or (during a rolling deploy) the legacy bare array.
    const steps = Array.isArray(payload) ? payload : payload.steps || [];
    const v = Array.isArray(payload) ? 1 : payload.version || 1;
    const cv = Array.isArray(payload)
      ? 1
      : payload.current_version || payload.version || 1;
    setSeq(steps);
    setSeqDraft(steps.map((s) => ({ ...s })));
    setSeqDirty(false);
    setSeqVersion(v);
    setCurrentSeqVersion(cv);
    if (versionsResp?.data?.versions) {
      setSeqVersions(versionsResp.data.versions);
    } else {
      setSeqVersions([{ version: cv, step_count: steps.length }]);
    }
  };

  const load = async () => {
    try {
      const [c, a, l] = await Promise.all([
        api.get(`/campaigns/${id}`),
        api.get(`/analytics/campaign/${id}`),
        api.get(`/campaigns/${id}/leads`),
      ]);
      setCampaign(c.data);
      setAnalytics(a.data);
      setLeads(l.data);
      await loadSequence();
    } catch {
      toast.error("Failed to load campaign");
    }
  };

  useEffect(() => {
    load();
  }, [id]);

  useEffect(() => {
    if (tab === "leads") fetchEnrichStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, id]);

  const start = async () => {
    setBusy(true);
    try {
      await api.post(`/campaigns/${id}/start`);
      toast.success("Campaign activated - first sends will go out within a minute");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start");
    } finally {
      setBusy(false);
    }
  };

  const pause = async () => {
    await api.post(`/campaigns/${id}/pause`);
    toast.success("Paused");
    load();
  };

  const remove = () => setDeleteOpen(true);

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/campaigns/${id}`);
      toast.success("Deleted");
      setDeleteOpen(false);
      navigate("/campaigns");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete");
    } finally {
      setDeleting(false);
    }
  };

  const importCsv = async () => {
    if (!csv.trim()) return;
    const blob = new Blob([csv], { type: "text/csv" });
    const fd = new FormData();
    fd.append("file", blob, "leads.csv");
    try {
      const r = await api.post(`/campaigns/${id}/leads/upload`, fd);
      toast.success(`Imported ${r.data.inserted} (skipped ${r.data.skipped})`);
      setCsv("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    }
  };

  // -------- Strike-1: AI icebreaker enrichment --------
  const fetchEnrichStatus = async () => {
    try {
      const r = await api.get(`/campaigns/${id}/leads/enrich/status`);
      setEnrichInfo(r.data);
    } catch {
      /* non-fatal */
    }
  };

  const pollEnrich = (times) => {
    let n = 0;
    const tick = async () => {
      await fetchEnrichStatus();
      await load();
      n += 1;
      if (n < times) setTimeout(tick, 4000);
    };
    setTimeout(tick, 2500);
  };

  const enrichLeads = async () => {
    setEnrichBusy(true);
    try {
      const r = await api.post(`/campaigns/${id}/leads/enrich`, {});
      toast.success(r.data.message || `Queued ${r.data.queued} leads`);
      pollEnrich(6);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI personalization failed");
    } finally {
      setEnrichBusy(false);
    }
  };

  // -------- Stage 8B: sequence inline editor --------
  const onSeqVersionChange = async (v) => {
    if (seqDirty) {
      // Strike 8C: defer the switch into a state-driven modal instead of
      // blocking the browser thread with window.confirm.
      setPendingVersionSwitch(v);
      return;
    }
    await loadSequence(v);
  };

  const confirmDiscardVersionSwitch = async () => {
    const v = pendingVersionSwitch;
    setPendingVersionSwitch(null);
    if (v != null) await loadSequence(v);
  };

  const updateDraftStep = (idx, patch) => {
    setSeqDraft((prev) => {
      const next = prev.map((s, i) => (i === idx ? { ...s, ...patch } : s));
      setSeqDirty(true);
      return next;
    });
  };

  const addEmptyStep = () => {
    setSeqDraft((prev) => {
      const stepNumber =
        prev.length > 0 ? Math.max(...prev.map((s) => s.step_number)) + 1 : 1;
      return [
        ...prev,
        {
          step_number: stepNumber,
          wait_days: prev.length === 0 ? 0 : 3,
          subject: "",
          body_html: "",
          body_text: "",
          ab_variants: [],
        },
      ];
    });
    setSeqDirty(true);
  };

  const removeDraftStep = (idx) => {
    setSeqDraft((prev) => {
      const filtered = prev.filter((_, i) => i !== idx);
      // Re-number from 1 so step_number stays contiguous — the sender's
      // `current_step + 1` cursor depends on this.
      return filtered.map((s, i) => ({ ...s, step_number: i + 1 }));
    });
    setSeqDirty(true);
  };

  const saveSequence = async () => {
    if (sequenceLocked) return;
    if (seqDraft.length === 0) {
      toast.error("Add at least one step before saving.");
      return;
    }
    const missing = seqDraft.findIndex(
      (s) => !s.subject?.trim() || !s.body_html?.trim(),
    );
    if (missing >= 0) {
      toast.error(`Step ${missing + 1}: subject and body are required.`);
      return;
    }
    setSeqSaving(true);
    try {
      const r = await api.put(`/campaigns/${id}/sequence`, {
        steps: seqDraft.map((s, i) => ({
          step_number: i + 1,
          wait_days: Number(s.wait_days) || 0,
          subject: s.subject,
          body_html: s.body_html,
          body_text: s.body_text || "",
          ab_variants: s.ab_variants || [],
        })),
      });
      if (r.data?.immutable_write) {
        toast.success(
          `Saved as version ${r.data.version}. In-flight leads keep running on v${r.data.version - 1}.`,
        );
      } else {
        toast.success(`Saved version ${r.data?.version || 1}`);
      }
      // Reload to pick up the new current_version + refreshed versions list.
      await loadSequence();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save sequence");
    } finally {
      setSeqSaving(false);
    }
  };

  const [exitingLeads, setExitingLeads] = useState({});

  const removeLead = async (lid) => {
    // Phase 2 layout-fluidity: play the row-exit animation first, THEN mutate.
    // The DOM row collapses smoothly instead of snapping out.
    setExitingLeads((s) => ({ ...s, [lid]: true }));
    setTimeout(async () => {
      try {
        await api.delete(`/leads/${lid}`);
      } finally {
        await load();
        setExitingLeads((s) => {
          const next = { ...s };
          delete next[lid];
          return next;
        });
      }
    }, 240);
  };

  if (!campaign) return <div className="text-neutral-500 text-sm font-mono">Loading…</div>;

  const tabs = [
    { id: "overview", label: "Overview" },
    { id: "sequence", label: "Sequence" },
    { id: "leads", label: "Leads" },
    { id: "analytics", label: "Analytics" },
    { id: "settings", label: "Settings" },
  ];

  return (
    <div className="space-y-6">
      <button
        onClick={() => navigate("/campaigns")}
        className="text-sm text-neutral-400 hover:text-white inline-flex items-center gap-1.5"
        data-testid="back-button"
      >
        <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> Back to campaigns
      </button>
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-2">
            <StatusBadge status={campaign.status} />
            <span className="text-xs text-neutral-500 font-mono uppercase tracking-[0.2em]">
              {campaign.id}
            </span>
          </div>
          <h1 className="font-display text-3xl font-semibold tracking-tight mt-2">
            {campaign.name}
          </h1>
        </div>
        <div className="flex items-center gap-2">
          {campaign.status === "active" ? (
            <button
              onClick={pause}
              data-testid="pause-campaign"
              className="flex items-center gap-2 px-4 py-2 border border-[#27272a] rounded-md text-sm hover:bg-[#161616]"
            >
              <Pause className="w-4 h-4" strokeWidth={1.5} /> Pause
            </button>
          ) : (
            <button
              onClick={start}
              disabled={busy}
              data-testid="activate-campaign"
              className="flex items-center gap-2 bg-white text-black px-4 py-2 rounded-md text-sm font-medium hover:bg-neutral-200"
            >
              <Play className="w-4 h-4" strokeWidth={1.8} /> Activate
            </button>
          )}
          <button
            onClick={remove}
            data-testid="delete-campaign"
            className="p-2 border border-[#27272a] rounded-md text-red-400 hover:bg-[#161616]"
          >
            <Trash2 className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>
      </div>

      <div className="border-b border-[#1a1a1a]">
        <div className="flex items-center gap-1">
          {tabs.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`tab-${t.id}`}
              className={`px-4 py-2 text-sm border-b-2 -mb-px transition-colors ${
                tab === t.id
                  ? "border-white text-white"
                  : "border-transparent text-neutral-500 hover:text-white"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {tab === "overview" && (
        <div className="space-y-4">
          {isAdmin ? (
            <CampaignDiagnose campaignId={id} onAction={load} />
          ) : (
            <div className="border border-[#1a1a1a] bg-[#0d0d0d] rounded-lg p-5 flex items-center justify-between gap-3 flex-wrap">
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-500 font-mono">
                  Sending status
                </div>
                <div className="mt-2">
                  <CampaignStatusPill campaign={campaign} />
                </div>
              </div>
              <button
                onClick={() => setTab("settings")}
                data-testid="overview-edit-schedule"
                className="text-xs font-mono uppercase tracking-[0.18em] text-neutral-400 hover:text-white border border-[#27272a] rounded-md px-3 py-2"
              >
                Edit schedule
              </button>
            </div>
          )}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {[
              ["Sent", analytics?.sent ?? 0],
              ["Open rate", `${analytics?.open_rate ?? 0}%`],
              ["Reply rate", `${analytics?.reply_rate ?? 0}%`],
              ["Click rate", `${analytics?.click_rate ?? 0}%`],
              ["Leads", analytics?.leads_total ?? 0],
              ["Bounced", analytics?.bounced ?? 0],
              ["Daily cap", campaign.daily_cap],
              ["Inboxes", (campaign.inbox_ids || []).length],
            ].map(([label, val]) => (
              <div
                key={label}
                className="border border-[#1a1a1a] bg-[#0d0d0d] rounded-lg p-4"
                data-testid={`overview-${label.toLowerCase().replace(/\s/g, "-")}`}
              >
                <div className="text-[10px] uppercase tracking-[0.18em] text-neutral-500 font-mono">
                  {label}
                </div>
                <div className="font-mono text-2xl mt-2">{val}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "sequence" && (
        <div className="space-y-5" data-testid="sequence-tab">
          {/* Stage 8B — Version History toolbar */}
          <div className="flex items-center flex-wrap gap-3 border border-[#1a1a1a] bg-[#0d0d0d] rounded-lg px-4 py-3">
            <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-[0.2em] text-neutral-500">
              <History className="w-3.5 h-3.5" strokeWidth={1.8} />
              Version
            </div>
            <select
              value={seqVersion ?? currentSeqVersion}
              onChange={(e) => onSeqVersionChange(Number(e.target.value))}
              data-testid="sequence-version-select"
              className="bg-[#0a0a0a] border border-[#1f1f1f] focus:border-[#2a2a2a] rounded-md px-2.5 py-1.5 text-xs text-white outline-none font-mono"
            >
              {seqVersions.map((v) => {
                const isCurrent = v.version === currentSeqVersion;
                return (
                  <option key={v.version} value={v.version}>
                    v{v.version} · {v.step_count} step
                    {v.step_count === 1 ? "" : "s"}
                    {isCurrent ? " · Current" : " · Legacy"}
                  </option>
                );
              })}
            </select>
            {isViewingLegacy && (
              <div
                data-testid="legacy-version-banner"
                className="inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-[0.2em] text-amber-300/90 bg-amber-300/[0.06] border border-amber-300/20 rounded-md px-2 py-1"
              >
                <Lock className="w-3 h-3" strokeWidth={1.8} />
                Read-only · Legacy revision
              </div>
            )}
            {!isViewingLegacy && campaign?.status === "completed" && (
              <div className="inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-[0.2em] text-neutral-400 bg-[#101010] border border-[#1f1f1f] rounded-md px-2 py-1">
                <Lock className="w-3 h-3" strokeWidth={1.8} />
                Read-only · Completed
              </div>
            )}
            <div className="ml-auto flex items-center gap-2">
              {seqDirty && !sequenceLocked && (
                <span className="text-[11px] font-mono uppercase tracking-[0.2em] text-amber-300/90">
                  Unsaved changes
                </span>
              )}
              {!sequenceLocked && (
                <>
                  <button
                    type="button"
                    onClick={addEmptyStep}
                    disabled={seqSaving}
                    data-testid="sequence-add-step"
                    className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-[0.2em] border border-[#1f1f1f] hover:border-[#2a2a2a] bg-[#0a0a0a] text-neutral-300 hover:text-white px-3 py-1.5 rounded-md"
                  >
                    <Plus className="w-3.5 h-3.5" strokeWidth={1.8} />
                    Add step
                  </button>
                  <button
                    type="button"
                    onClick={saveSequence}
                    disabled={seqSaving || !seqDirty}
                    data-testid="sequence-save"
                    className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-[0.2em] bg-white text-black hover:bg-neutral-200 px-3 py-1.5 rounded-md disabled:opacity-40"
                  >
                    <Save className="w-3.5 h-3.5" strokeWidth={1.8} />
                    {seqSaving ? "Saving…" : "Save"}
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Steps render — editable when on current version (and campaign
              not completed), read-only when viewing a legacy revision. */}
          {(sequenceLocked ? seq : seqDraft).length === 0 ? (
            <EmptyState
              title="No sequence steps"
              description={
                sequenceLocked
                  ? "This revision is empty."
                  : "Add at least one email step before activating."
              }
            />
          ) : (
            <div className="space-y-3">
              {(sequenceLocked ? seq : seqDraft).map((s, i) => (
                <div
                  key={`${seqVersion}-${s.step_number}-${i}`}
                  className="relative pl-6 border-l border-[#27272a]"
                  data-testid={`sequence-step-${i}`}
                >
                  <div
                    className="absolute -left-[7px] top-2 w-3 h-3 rounded-full bg-white border border-[#0a0a0a]"
                    aria-hidden
                  />
                  <div className="border border-[#1a1a1a] bg-[#0d0d0d] rounded-lg p-5 space-y-3">
                    <div className="text-xs font-mono uppercase tracking-[0.2em] text-neutral-500 flex items-center justify-between gap-2">
                      <span>Step {i + 1}</span>
                      <div className="flex items-center gap-3">
                        {i > 0 && (
                          <span className="inline-flex items-center gap-1.5">
                            Wait
                            {sequenceLocked ? (
                              <span className="text-neutral-300">
                                {s.wait_days}d
                              </span>
                            ) : (
                              <input
                                type="number"
                                min={0}
                                max={365}
                                value={s.wait_days ?? 0}
                                onChange={(e) =>
                                  updateDraftStep(i, {
                                    wait_days: Number(e.target.value),
                                  })
                                }
                                data-testid={`sequence-wait-${i}`}
                                className="w-14 bg-[#0a0a0a] border border-[#1f1f1f] rounded px-2 py-0.5 text-[11px] text-white outline-none focus:border-[#2a2a2a]"
                              />
                            )}
                            d
                          </span>
                        )}
                        {!sequenceLocked && (
                          <button
                            type="button"
                            onClick={() => removeDraftStep(i)}
                            data-testid={`sequence-remove-${i}`}
                            className="text-neutral-500 hover:text-red-400 transition-colors"
                            aria-label="Remove step"
                            title="Remove step"
                          >
                            <X className="w-3.5 h-3.5" strokeWidth={1.8} />
                          </button>
                        )}
                      </div>
                    </div>

                    {sequenceLocked ? (
                      <>
                        <div className="text-sm text-white">{s.subject}</div>
                        <pre className="text-xs text-neutral-400 font-mono whitespace-pre-wrap leading-relaxed">
                          {s.body_html}
                        </pre>
                      </>
                    ) : (
                      <>
                        <input
                          value={s.subject || ""}
                          onChange={(e) =>
                            updateDraftStep(i, { subject: e.target.value })
                          }
                          placeholder="Subject"
                          data-testid={`sequence-subject-${i}`}
                          className="w-full bg-[#0a0a0a] border border-[#1f1f1f] focus:border-[#2a2a2a] rounded-md px-3 py-2 text-sm text-white outline-none"
                        />
                        <div className="flex items-center justify-between">
                          <span className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-mono">
                            {s.htmlMode ? "HTML source" : "Body"}
                          </span>
                          <button
                            type="button"
                            onClick={() =>
                              updateDraftStep(i, { htmlMode: !s.htmlMode })
                            }
                            data-testid={`sequence-html-toggle-${i}`}
                            title="Toggle raw HTML code view"
                            className={`inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.18em] px-2 py-1 rounded border transition-colors ${
                              s.htmlMode
                                ? "bg-violet-500/15 border-violet-500/40 text-violet-200"
                                : "bg-[#0a0a0a] border-[#27272a] text-neutral-400 hover:bg-[#161616]"
                            }`}
                          >
                            <Code className="w-3 h-3" strokeWidth={2} /> HTML
                          </button>
                        </div>
                        <textarea
                          value={s.body_html || ""}
                          onChange={(e) =>
                            updateDraftStep(i, { body_html: e.target.value })
                          }
                          placeholder={
                            s.htmlMode
                              ? `<p>Hey {{first_name}},</p>\n<a href="{{LoomURL}}"><img src="{{LoomThumbnail}}" /></a>`
                              : "Body (HTML or plain text). Supports {{first_name}}, {{company}}, {{custom.x}} merge tags."
                          }
                          rows={s.htmlMode ? 9 : 6}
                          data-testid={`sequence-body-${i}`}
                          className={`w-full bg-[#0a0a0a] border focus:border-[#2a2a2a] rounded-md px-3 py-2 text-xs text-neutral-200 font-mono outline-none leading-relaxed resize-y ${
                            s.htmlMode ? "border-violet-500/40" : "border-[#1f1f1f]"
                          }`}
                        />
                        {s.htmlMode && (
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-mono mr-1">
                              Loom
                            </span>
                            {["LoomURL", "LoomThumbnail"].map((v) => (
                              <button
                                key={v}
                                type="button"
                                onClick={() =>
                                  updateDraftStep(i, {
                                    body_html: `${s.body_html || ""} {{${v}}} `,
                                  })
                                }
                                data-testid={`sequence-insert-${v}-${i}`}
                                className="var-chip border border-violet-500/40 text-violet-200 hover:bg-violet-500 hover:text-white"
                              >
                                {`{{${v}}}`}
                              </button>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "leads" && (
        <div className="space-y-4">
          <div className="border border-[#1a1a1a] bg-[#0d0d0d] rounded-lg p-5">
            <div className="text-xs font-mono uppercase tracking-[0.2em] text-neutral-500 mb-3">
              Import leads (CSV)
            </div>
            <textarea
              value={csv}
              onChange={(e) => setCsv(e.target.value)}
              placeholder={`email,first_name,company\njane@acme.com,Jane,Acme`}
              rows={6}
              data-testid="leads-csv-input"
              className="w-full bg-[#0a0a0a] border border-[#27272a] rounded-md px-3 py-2 text-xs font-mono"
            />
            <div className="mt-3 flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-2 text-xs font-mono text-neutral-500">
                <Sparkles className="w-3.5 h-3.5 text-violet-400" strokeWidth={1.8} />
                {enrichInfo ? (
                  <span data-testid="enrich-status">
                    {enrichInfo.with_icebreaker}/{enrichInfo.total_leads} personalized
                    {" · "}
                    {enrichInfo.global_quota_remaining}/{enrichInfo.global_hourly_limit} AI credits left this hour
                  </span>
                ) : (
                  <span>AI icebreakers populate custom field ai_icebreaker</span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={enrichLeads}
                  disabled={enrichBusy}
                  data-testid="enrich-leads-button"
                  className="flex items-center gap-2 border border-violet-500/40 bg-violet-500/10 text-violet-200 px-4 py-2 rounded-md text-sm font-medium hover:bg-violet-500/20 disabled:opacity-50"
                >
                  {enrichBusy ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={1.8} />
                  ) : (
                    <Sparkles className="w-3.5 h-3.5" strokeWidth={1.8} />
                  )}
                  AI personalize
                </button>
                <button
                  onClick={importCsv}
                  data-testid="import-leads-button"
                  className="flex items-center gap-2 bg-white text-black px-4 py-2 rounded-md text-sm font-medium"
                >
                  <Upload className="w-3.5 h-3.5" strokeWidth={1.8} /> Import
                </button>
              </div>
            </div>
          </div>
          {leads.length === 0 ? (
            <EmptyState
              title="No leads yet"
              description="Upload a CSV to populate this campaign."
            />
          ) : (
            <div className="border border-[#1a1a1a] bg-[#0d0d0d] rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="text-[10px] uppercase tracking-[0.18em] text-neutral-500 font-mono">
                  <tr className="border-b border-[#1a1a1a]">
                    <th className="text-left px-6 py-3">Email</th>
                    <th className="text-left px-6 py-3">Name</th>
                    <th className="text-left px-6 py-3">Company</th>
                    <th className="text-left px-6 py-3">Status</th>
                    <th className="text-left px-6 py-3">Icebreaker</th>
                    <th className="text-right px-6 py-3">Step</th>
                    <th className="text-right px-6 py-3">Opens</th>
                    <th />
                  </tr>
                </thead>
                <tbody className="font-mono text-sm">
                  {leads.slice(0, 200).map((l) => (
                    <tr
                      key={l.id}
                      className={`tactile border-b border-[#1a1a1a] hover:bg-[#111] ${
                        exitingLeads[l.id] ? "row-exiting" : ""
                      }`}
                      data-testid={`lead-row-${l.id}`}
                    >
                      <td className="px-6 py-2.5">{l.email}</td>
                      <td className="px-6 py-2.5 text-neutral-300">
                        {l.first_name} {l.last_name}
                      </td>
                      <td className="px-6 py-2.5 text-neutral-400">{l.company}</td>
                      <td className="px-6 py-2.5">
                        <StatusBadge status={l.status} />
                      </td>
                      <td className="px-6 py-2.5 max-w-[260px]">
                        {l.custom_fields?.ai_icebreaker ? (
                          <span
                            className="text-violet-200/90 text-xs line-clamp-1"
                            title={l.custom_fields.ai_icebreaker}
                            data-testid={`lead-icebreaker-${l.id}`}
                          >
                            {l.custom_fields.ai_icebreaker}
                          </span>
                        ) : (
                          <span className="text-neutral-600">—</span>
                        )}
                      </td>
                      <td className="px-6 py-2.5 text-right">{l.current_step}</td>
                      <td className="px-6 py-2.5 text-right">{l.opens || 0}</td>
                      <td className="px-6 py-2.5 text-right">
                        <button
                          onClick={() => removeLead(l.id)}
                          className="text-neutral-500 hover:text-red-400"
                          data-testid={`delete-lead-${l.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {leads.length > 200 && (
                <div className="text-xs text-neutral-500 px-6 py-3 border-t border-[#1a1a1a] font-mono">
                  Showing first 200 of {leads.length} leads
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {tab === "analytics" && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {[
              ["Sent", analytics?.sent ?? 0],
              ["Open rate", `${analytics?.open_rate ?? 0}%`],
              ["Click rate", `${analytics?.click_rate ?? 0}%`],
              ["Reply rate", `${analytics?.reply_rate ?? 0}%`],
            ].map(([label, val]) => (
              <div
                key={label}
                className="border border-[#1a1a1a] bg-[#0d0d0d] rounded-lg p-4"
              >
                <div className="text-[10px] uppercase tracking-[0.18em] text-neutral-500 font-mono">
                  {label}
                </div>
                <div className="font-mono text-2xl mt-2">{val}</div>
              </div>
            ))}
          </div>
          <div className="border border-[#1a1a1a] bg-[#0d0d0d] rounded-lg overflow-hidden">
            <div className="px-6 py-4 border-b border-[#1a1a1a] text-xs font-mono uppercase tracking-[0.2em] text-neutral-500">
              Step funnel
            </div>
            <table className="w-full">
              <thead className="text-[10px] uppercase tracking-[0.18em] text-neutral-500 font-mono">
                <tr className="border-b border-[#1a1a1a]">
                  <th className="text-left px-6 py-3">Step</th>
                  <th className="text-left px-6 py-3">Subject</th>
                  <th className="text-right px-6 py-3">Sent</th>
                  <th className="text-right px-6 py-3">Opened</th>
                  <th className="text-right px-6 py-3">Clicked</th>
                </tr>
              </thead>
              <tbody className="font-mono text-sm">
                {(analytics?.funnel || []).map((s) => (
                  <tr key={s.step_number} className="border-b border-[#1a1a1a]">
                    <td className="px-6 py-3">#{s.step_number}</td>
                    <td className="px-6 py-3 text-white">{s.subject}</td>
                    <td className="px-6 py-3 text-right">{s.sent}</td>
                    <td className="px-6 py-3 text-right">{s.opened}</td>
                    <td className="px-6 py-3 text-right">{s.clicked}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "settings" && (
        <CampaignSettings campaign={campaign} onSaved={load} />
      )}

      <ConfirmModal
        open={deleteOpen}
        onClose={() => (deleting ? null : setDeleteOpen(false))}
        onConfirm={confirmDelete}
        busy={deleting}
        kicker={`Campaign · ${campaign?.name || ""}`}
        title="Delete this campaign?"
        description={
          <>
            This permanently removes{" "}
            <span className="font-mono text-white">{campaign?.name}</span>,
            every lead attached to it, the entire send log, and all sequence
            revisions. This cannot be undone.
          </>
        }
        confirmLabel="Delete campaign"
        variant="destructive"
        testId="campaign-delete-confirm"
      />

      <ConfirmModal
        open={pendingVersionSwitch !== null}
        onClose={() => setPendingVersionSwitch(null)}
        onConfirm={confirmDiscardVersionSwitch}
        kicker="Sequence · Unsaved changes"
        title="Discard unsaved changes?"
        description={
          <>
            You have unsaved edits on{" "}
            <span className="font-mono text-white">
              v{currentSeqVersion}
            </span>
            . Switching to{" "}
            <span className="font-mono text-white">
              v{pendingVersionSwitch ?? ""}
            </span>{" "}
            will drop them. This cannot be undone.
          </>
        }
        confirmLabel="Discard changes"
        variant="destructive"
        testId="sequence-discard-confirm"
      />
    </div>
  );
}

function CampaignSettings({ campaign, onSaved }) {
  const [tz, setTz] = useState(campaign.timezone || "UTC");
  const [win, setWin] = useState(
    campaign.sending_window || { start_hour: 0, end_hour: 24, days: [0, 1, 2, 3, 4, 5, 6] },
  );
  const [dailyCap, setDailyCap] = useState(campaign.daily_cap || 100);
  const [intervalMin, setIntervalMin] = useState(campaign.send_interval_minutes || 0);
  const [trackOpens, setTrackOpens] = useState(campaign.track_opens !== false);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/campaigns/${campaign.id}`, {
        timezone: tz,
        sending_window: win,
        daily_cap: parseInt(dailyCap) || 100,
        send_interval_minutes: Math.max(0, parseInt(intervalMin) || 0),
        track_opens: trackOpens,
      });
      toast.success("Settings saved");
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4 max-w-3xl">
      <div className="border border-[#1a1a1a] bg-[#0d0d0d] rounded-lg p-6">
        <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-500 font-mono mb-4">
          Schedule
        </div>
        <SendingWindowEditor
          timezone={tz}
          sendingWindow={win}
          onChange={(patch) => {
            if (patch.timezone !== undefined) setTz(patch.timezone);
            if (patch.sending_window) setWin(patch.sending_window);
          }}
          testIdPrefix="settings"
        />
      </div>
      <div className="border border-[#1a1a1a] bg-[#0d0d0d] rounded-lg p-6">
        <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-500 font-mono mb-4">
          Throughput
        </div>
        <div className="space-y-4">
          <div className="flex items-center gap-4 flex-wrap">
            <span className="text-sm text-neutral-400 w-44">Daily cap</span>
            <input
              type="number"
              min={1}
              value={dailyCap}
              onChange={(e) => setDailyCap(e.target.value)}
              data-testid="settings-daily-cap"
              className="w-32 bg-[#0a0a0a] border border-[#27272a] rounded-md px-3 py-2 text-sm font-mono"
            />
            <span className="text-xs text-neutral-500 font-mono">
              sends/day across all inboxes
            </span>
          </div>
          <div className="flex items-start gap-4 flex-wrap">
            <span className="text-sm text-neutral-400 w-44 pt-2">
              Min minutes between sends
            </span>
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2 flex-wrap">
                <input
                  type="number"
                  min={0}
                  max={1440}
                  value={intervalMin}
                  onChange={(e) => setIntervalMin(e.target.value)}
                  data-testid="settings-send-interval"
                  className="w-32 bg-[#0a0a0a] border border-[#27272a] rounded-md px-3 py-2 text-sm font-mono"
                />
                <div className="flex gap-1.5">
                  {[0, 2, 5, 10, 20].map((p) => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => setIntervalMin(p)}
                      data-testid={`settings-interval-preset-${p}`}
                      className={`text-[10px] uppercase tracking-[0.18em] font-mono px-2 py-1 rounded border transition-colors ${
                        Number(intervalMin) === p
                          ? "bg-white text-black border-white"
                          : "bg-[#0a0a0a] border-[#27272a] text-neutral-400 hover:bg-[#161616]"
                      }`}
                    >
                      {p === 0 ? "Off" : `${p}m`}
                    </button>
                  ))}
                </div>
              </div>
              <p className="text-xs text-neutral-500 leading-relaxed max-w-md">
                0 = no rate limit. Actual interval is jittered −30%/+40% per send so
                Gmail doesn't see a perfectly periodic pattern.
              </p>
            </div>
          </div>
        </div>
      </div>
      <div className="border border-[#1a1a1a] bg-[#0d0d0d] rounded-lg p-6">
        <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-500 font-mono mb-4">
          Deliverability
        </div>
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-sm text-neutral-300">Open tracking pixel</div>
            <p className="text-xs text-neutral-500 leading-relaxed max-w-md mt-1">
              When off, OutreachOS will not embed the 1×1 open-tracking pixel in
              outgoing emails. Click tracking still works. Disabling can improve
              deliverability against strict spam filters.
            </p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={trackOpens}
            onClick={() => setTrackOpens((v) => !v)}
            data-testid="settings-track-opens"
            className={`relative shrink-0 w-11 h-6 rounded-full transition-colors ${
              trackOpens ? "bg-white" : "bg-[#27272a]"
            }`}
          >
            <span
              className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full transition-transform ${
                trackOpens ? "translate-x-5 bg-black" : "translate-x-0 bg-neutral-400"
              }`}
            />
          </button>
        </div>
      </div>
      <div className="flex items-center justify-end">
        <button
          onClick={save}
          disabled={busy}
          data-testid="settings-save"
          className="flex items-center gap-2 bg-white text-black px-5 py-2.5 rounded-md text-sm font-medium hover:bg-neutral-200 disabled:opacity-50"
        >
          <Save className="w-3.5 h-3.5" strokeWidth={1.8} />
          Save settings
        </button>
      </div>
    </div>
  );
}
